/* Created by Interactive 63 - https://interactive63.com */
/**
 * The offset animation popover: shifts each selected element's keyframes later by a growing
 * amount so identical animations cascade. One undo step.
 */
import { offsetAnimation } from '../../core/presets/apply';
import { STAGGER_ORDERS, type StaggerOrder } from '../../core/presets/stagger';
import type { ElementId } from '../../core/model/types';
import type { StagePanel } from '../panels/stage/stage';
import type { Store } from '../store/store';
import { h } from '../ui/dom';
import { createPopover, type Popover } from '../ui/popover';
import { toast } from '../ui/toast';

export class OffsetPopover {
  private readonly popover: Popover;
  private readonly content: HTMLElement;
  private offsetMs = 100;
  private order: StaggerOrder = 'layer';
  private seed = 1;
  private extendScene = true;

  /** Creates the popover and attaches it to the page. */
  constructor(
    private readonly store: Store,
    private readonly stage: StagePanel,
  ) {
    this.content = h('div', { class: 'offset-panel' });
    this.popover = createPopover(h('span'), this.content, 'Offset animation');
    document.body.append(this.popover.el);
  }

  /** The selected elements, in layer order. */
  private targets(): ElementId[] {
    const { project, editor } = this.store.getState();
    const chosen = new Set(editor.selection.elementIds);
    return Object.keys(project.elements).filter((id) => chosen.has(id));
  }

  /** Opens the popover at a screen position. */
  open(x: number, y: number): void {
    this.render();
    this.popover.open();
    const el = this.popover.el;
    el.style.left = `${Math.max(8, Math.min(window.innerWidth - el.offsetWidth - 8, x))}px`;
    el.style.top = `${Math.max(8, Math.min(window.innerHeight - el.offsetHeight - 8, y))}px`;
  }

  /** Builds the fields and the Apply button. */
  private render(): void {
    const ids = this.targets();
    const offset = h('input', {
      type: 'number',
      min: 0,
      max: 5000,
      step: 10,
      value: this.offsetMs,
      'aria-label': 'Offset in milliseconds',
    });
    offset.addEventListener('change', () => {
      this.offsetMs = Math.max(0, Number(offset.value) || 0);
    });
    const order = h(
      'select',
      { 'aria-label': 'Offset order' },
      ...STAGGER_ORDERS.map((o) =>
        h('option', { value: o.id, selected: o.id === this.order }, o.label),
      ),
    );
    order.addEventListener('change', () => {
      this.order = order.value as StaggerOrder;
      this.render();
    });
    const extend = h('input', { type: 'checkbox', checked: this.extendScene });
    extend.addEventListener('change', () => {
      this.extendScene = extend.checked;
    });
    const apply = h(
      'button',
      { type: 'button', class: 'primary', disabled: ids.length < 2 },
      'Apply',
    );
    apply.addEventListener('click', () => {
      this.apply(ids);
    });
    const seed = h('input', {
      type: 'number',
      min: 0,
      max: 99999,
      value: this.seed,
      'aria-label': 'Seed',
    });
    seed.addEventListener('change', () => {
      this.seed = Math.round(Number(seed.value) || 0);
    });
    const hint =
      ids.length < 2 ? h('p', { class: 'presets-hint' }, 'Select two or more elements.') : null;
    const seedRow =
      this.order === 'random'
        ? h('label', { class: 'presets-row' }, h('span', null, 'Seed'), seed)
        : null;
    this.content.replaceChildren(
      ...(
        [
          h('h3', null, 'Offset animation'),
          hint,
          h('label', { class: 'presets-row' }, h('span', null, 'Offset (ms)'), offset),
          h('label', { class: 'presets-row' }, h('span', null, 'Order'), order),
          seedRow,
          h('label', { class: 'presets-check' }, extend, ' Extend scene to fit'),
          h('div', { class: 'presets-actions' }, apply),
        ] as (HTMLElement | null)[]
      ).filter((node): node is HTMLElement => node !== null),
    );
  }

  /** Applies the offset as one undo step. */
  private apply(ids: ElementId[]): void {
    const centres: Record<ElementId, { x: number; y: number }> = {};
    for (const id of ids) {
      const box = this.stage.getElement(id)?.getBoundingClientRect();
      if (box) centres[id] = { x: box.left + box.width / 2, y: box.top + box.height / 2 };
    }
    let shifted = 0;
    this.store.updateProject('Offset animation', (draft) => {
      shifted = offsetAnimation(draft, ids, {
        offsetMs: this.offsetMs,
        order: this.order,
        seed: this.seed,
        extendScene: this.extendScene,
        centres,
      }).shifted.length;
    });
    this.popover.close();
    if (shifted === 0) toast.info('None of the selected elements have animation to offset.');
  }
}
